if (!customElements.get('site-search')) {
  customElements.define(
    'site-search',
    class SiteSearch extends HTMLElement {
      connectedCallback() {
        // Lives on <body> so the fixed panel is never clipped by the header.
        if (this.parentElement !== document.body) {
          document.querySelectorAll('body > site-search').forEach((old) => old.remove());
          document.body.appendChild(this);
          return;
        }
        if (this.initialized) return;
        this.initialized = true;

        this.input = this.querySelector('[data-site-search-input]');
        this.results = this.querySelector('[data-site-search-results]');
        this.start = this.querySelector('[data-site-search-start]');
        this.clearButton = this.querySelector('[data-site-search-clear]');
        this.cache = {};

        this.onDocumentClick = this.onDocumentClick.bind(this);
        document.addEventListener('click', this.onDocumentClick);

        this.querySelectorAll('[data-site-search-close]').forEach((el) =>
          el.addEventListener('click', () => this.close())
        );
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.close();
        });
        this.clearButton.addEventListener('click', () => {
          this.input.value = '';
          this.update();
          this.input.focus();
        });

        // Phones: scrolling the results closes the keyboard (like in apps).
        // With the keyboard open, iPhone Safari left bits of old cards
        // (titles, prices) painted just above it, over the results.
        const panel = this.querySelector('.site-search__panel');
        const phone = window.matchMedia('(max-width: 749px)');
        if (panel) {
          panel.addEventListener(
            'touchmove',
            () => {
              if (phone.matches && document.activeElement === this.input) this.input.blur();
            },
            { passive: true }
          );
        }

        let timer;
        this.input.addEventListener('input', () => {
          clearTimeout(timer);
          timer = setTimeout(() => this.update(), 220);
        });
      }

      disconnectedCallback() {
        if (this.initialized && !this.isConnected) document.removeEventListener('click', this.onDocumentClick);
      }

      onDocumentClick(event) {
        const trigger = event.target.closest('[data-site-search-open]');
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      }

      open(trigger) {
        this.trigger = trigger;
        this.hidden = false;
        document.body.classList.add('overflow-hidden');
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          setTimeout(() => {
            this.input.focus();
            if (typeof trapFocus === 'function') trapFocus(this.querySelector('.site-search__panel'), this.input);
          }, 60);
        });
      }

      close() {
        if (this.hidden) return;
        this.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.trigger);
        clearTimeout(this.hideTimer);
        this.hideTimer = setTimeout(() => {
          if (!this.classList.contains('is-open')) this.hidden = true;
        }, 380);
      }

      update() {
        const query = this.input.value.trim();
        this.clearButton.hidden = query.length === 0;

        if (!query) {
          if (this.controller) this.controller.abort();
          this.classList.remove('is-loading', 'has-results');
          this.results.innerHTML = '';
          this.start.hidden = false;
          return;
        }

        if (this.cache[query]) {
          this.render(this.cache[query]);
          return;
        }

        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        this.classList.add('is-loading');

        const url =
          `${this.dataset.resultsUrl}?q=${encodeURIComponent(query)}` +
          '&section_id=site-search-results' +
          '&resources[type]=product,collection,page,query' +
          '&resources[limit]=8&resources[limit_scope]=each';

        fetch(url, { signal: this.controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((text) => {
            const section = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('#shopify-section-site-search-results');
            const html = section ? section.innerHTML : '';
            this.cache[query] = html;
            if (this.input.value.trim() === query) this.render(html);
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            this.classList.remove('is-loading');
          });
      }

      render(html) {
        this.classList.remove('is-loading');
        this.classList.add('has-results');
        this.results.innerHTML = html;
        // No result: keep the quick links under the message.
        this.start.hidden = !this.results.querySelector('.site-search__empty');
      }
    }
  );
}
